import React, { useEffect, useState } from 'react';
import { Star, Users, TrendingUp, MessageSquareQuote, Send, ThumbsUp, BarChart3 } from 'lucide-react';
import api from '../api/client';
import FormDialog from '../components/FormDialog';
import { toast } from '../hooks/use-toast';
import FileQuestionnaires from '../components/FileQuestionnaires';

export default function Evaluations() {
  const [evaluations, setEvaluations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get('/evaluations').then(({ data }) => setEvaluations(data)).catch(() => {});
    api.get('/sessions').then(({ data }) => setSessions(data)).catch(() => {});
  }, []);

  const handleSend = async (values) => {
    toast({ title: 'Enquête envoyée', description: `"${values.session}" - ${values.type}` });
  };

  const avgGlobal = evaluations.length ? (evaluations.reduce((s, e) => s + e.avg, 0) / evaluations.length).toFixed(1) : '0.0';
  const totalRespondents = evaluations.reduce((s, e) => s + e.respondents, 0);
  const totalMax = evaluations.reduce((s, e) => s + e.max, 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Note moyenne', value: `${avgGlobal}/10`, icon: Star, color: '#F59E0B' },
          { label: 'Répondants', value: totalRespondents, icon: Users, color: '#4F46E5' },
          { label: 'Taux de réponse', value: `${Math.round((totalRespondents / totalMax) * 100)}%`, icon: BarChart3, color: '#14B8A6' },
          { label: 'Satisfaction', value: '94%', icon: ThumbsUp, color: '#3B82F6' },
        ].map((s, i) => (
          <div key={i} className="soft-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${s.color}14` }}>
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
              </div>
            </div>
            <div className="text-[12px] text-[#8E8FA5] font-medium">{s.label}</div>
            <div className="text-[22px] font-bold text-[#1A1B2E] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <FileQuestionnaires />

      {/* Sessions evaluations */}
      <div className="soft-card overflow-hidden">
        <div className="px-5 py-4 border-b border-[#EFF0F6] flex items-center justify-between">
          <div>
            <h3 className="text-[15.5px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Évaluations par session</h3>
            <p className="text-[12.5px] text-[#8E8FA5]">Résultats des enquêtes à chaud et à froid</p>
          </div>
          <button onClick={() => setOpen(true)} className="h-9 px-3.5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 shadow-sm hover:opacity-95">
            <Send className="w-4 h-4" /> Envoyer une enquête
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] border-b border-[#EFF0F6]">
              <th className="py-3.5 px-5">Session</th>
              <th className="py-3.5 px-5">Note</th>
              <th className="py-3.5 px-5">Répondants</th>
              <th className="py-3.5 px-5">Satisfaction</th>
              <th className="py-3.5 px-5">Tendance</th>
            </tr>
          </thead>
          <tbody>
            {evaluations.map((e) => (
              <tr key={e.id} className="data-row border-b border-[#F3F4F8] last:border-0">
                <td className="py-4 px-5">
                  <div className="text-[13px] font-semibold text-[#1A1B2E]">{e.session}</div>
                  <div className="text-[11.5px] text-[#8E8FA5]">{e.program}</div>
                </td>
                <td className="py-4 px-5">
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#FEF3E7]">
                    <Star className="w-3.5 h-3.5 text-[#F59E0B] fill-[#F59E0B]" />
                    <span className="text-[13px] font-bold text-[#B45309]">{e.avg}/10</span>
                  </div>
                </td>
                <td className="py-4 px-5">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-[#1A1B2E]">{e.respondents}/{e.max}</span>
                    <span className="text-[11px] text-[#8E8FA5]">({Math.round((e.respondents / e.max) * 100)}%)</span>
                  </div>
                </td>
                <td className="py-4 px-5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 max-w-[120px] h-1.5 bg-[#F3F4F8] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#14B8A6]" style={{ width: `${e.satisfaction}%` }} />
                    </div>
                    <span className="text-[12.5px] font-bold text-[#0F766E] w-10 text-right">{e.satisfaction}%</span>
                  </div>
                </td>
                <td className="py-4 px-5">
                  <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0F766E]">
                    <TrendingUp className="w-3 h-3" /> +{Math.floor(Math.random() * 5 + 2)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Trois temoignages vivaient ici en dur, avec des noms de personnes
          et des formations qui n'existent pas. La plateforme recueille de
          vraies reponses libres dans ses questionnaires de satisfaction ;
          les relire est la tranche qui remplira cette place. */}
      <div className="soft-card p-5">
        <div className="flex items-center gap-2">
          <MessageSquareQuote className="w-4 h-4 text-[#C7D2FE]" />
          <h3 className="text-[15.5px] font-semibold text-[#1A1B2E]"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Derniers témoignages
          </h3>
        </div>
        <p className="text-[12.5px] text-[#8E8FA5] mt-1">
          Les réponses libres de vos questionnaires de satisfaction viendront ici :
          elles sont recueillies, elles ne sont pas encore relues.
        </p>
      </div>

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="Envoyer une enquête d'évaluation"
        description="Choisissez la session et le type d'enquête à envoyer aux apprenants."
        submitLabel="Envoyer"
        onSubmit={handleSend}
        fields={[
          { name: 'session', label: 'Session concernée', type: 'select', options: sessions.map((s) => s.title), required: true, full: true },
          { name: 'type', label: "Type d'enquête", type: 'select', options: ['À chaud', 'À froid (3 mois)', 'Complète'], required: true, full: true },
          { name: 'message', label: 'Message personnalisé', type: 'textarea', full: true, placeholder: 'Bonjour, merci de prendre 3 minutes pour évaluer…' },
        ]}
      />
    </div>
  );
}
